import { debounce, isEqual } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { fetchQuery, useQueryLoader, useRelayEnvironment } from "react-relay/hooks";

import type { GraphQLTaggedNode, PreloadedQuery, VariablesOf } from "react-relay/hooks";
import type { OperationType, Subscription } from "relay-runtime";

const DEFAULT_DEBOUNCE_MS = 100;

export const useQueryLoaderWithManualFetchToAvoidSuspense = <TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  debounceTime?: number
): [
  PreloadedQuery<TQuery> | null | undefined,
  (variables: VariablesOf<TQuery>, force?: boolean) => void,
  boolean,
  Error | undefined,
] => {
  const relayEnvironment = useRelayEnvironment();

  const [queryReference, loadQuery] = useQueryLoader<TQuery>(query);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [pendingRequest, setPendingRequest] = useState<Subscription | null>(null);
  const [lastVariables, setLastVariables] = useState<VariablesOf<TQuery> | null>(null);

  const fetchImpl = useCallback(
    (variables: VariablesOf<TQuery>, force?: boolean) => {
      if (isEqual(variables, lastVariables) && !force) {
        return;
      }
      setLastVariables(variables);
      // if we have a pending request in flight that's not done yet, cancel it
      if (pendingRequest && !pendingRequest.closed) {
        pendingRequest.unsubscribe();
      }
      setError(undefined);
      // fetchQuery will fetch the query and write
      // the data to the Relay store. This will ensure
      // that when we re-render, the data is already
      // cached and we don't suspend
      setPendingRequest(
        fetchQuery(relayEnvironment, query, variables).subscribe({
          complete: () => {
            // *After* the query has been fetched, we call
            // loadQuery again to re-render with a new
            // queryRef.
            // At this point the data for the query should
            // be cached, so we use the 'store-only'
            // fetchPolicy to avoid suspending.
            loadQuery(variables);
            setPendingRequest(null);
          },
          error: (error: Error) => {
            setPendingRequest(null);
            setError(error);
          },
        })
      );
    },
    [
      relayEnvironment,
      pendingRequest,
      setError,
      setPendingRequest,
      query,
      loadQuery,
      lastVariables,
      setLastVariables,
    ]
  );

  const fetch = useMemo(
    () => debounce(fetchImpl, debounceTime ?? DEFAULT_DEBOUNCE_MS),
    [debounceTime, fetchImpl]
  );

  return [queryReference, fetch, pendingRequest != null, error];
};
